import Theme from "./lexical_plugins/Theme";
import "../css/markdownEditor.css"
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { TRANSFORMERS } from "@lexical/markdown";
import { $convertFromMarkdownString, $convertToMarkdownString} from "@lexical/markdown";
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { SELECTION_CHANGE_COMMAND, $getSelection, $isRangeSelection } from 'lexical';
import { useEffect, useRef } from 'react';

import { PLAYGROUND_TRANSFORMERS } from "./lexical_plugins/MarkdownTransformers.ts";
import CodeHighlightPlugin from "./lexical_plugins/CodeHighlightPlugin";
import ToolbarPlugin from "./lexical_plugins/ToolbarPlugin";
import { DetailRequestButtonNode } from "./lexical_plugins/nodes/DetailRequestButtonNode";
import { TableMenuButtonNode } from "./lexical_plugins/nodes/TableMenuButtonNode";
import ErrorBoundary from "./ErrorBoundary";
import HeaderDetailRequestPlugin from "./lexical_plugins/HeaderDetailRequestPlugin";
import MarkdownTablePlugin from "./lexical_plugins/MarkdownTablePlugin";
import TableInteractionPlugin from "./lexical_plugins/TableInteractionPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";

import "../css/editor.css"

function Placeholder() {
  return (
    <div className="editor-placeholder">
      
    </div>
  );
}

// Plugin to track text selection changes
function SelectionChangePlugin({ onSelectionChange }) {
  const [editor] = useLexicalComposerContext();
  const isSelectingRef = useRef(false);
  
  useEffect(() => {
    let isMouseDown = false;
    let isTouchDown = false;
    let selectionTimeout = null;
    
    const handleMouseDown = () => {
      isMouseDown = true;
    };
    
    const handleMouseUp = () => {
      isMouseDown = false;
      // Delay checking selection to allow for final selection update
      if (selectionTimeout) clearTimeout(selectionTimeout);
      selectionTimeout = setTimeout(() => {
        if (isSelectingRef.current && !isTouchDown) {
          isSelectingRef.current = false;
          onSelectionChange?.(false);
        }
      }, 100);
    };
    
    const handleTouchStart = () => {
      isTouchDown = true;
    };
    
    const handleTouchEnd = () => {
      isTouchDown = false;
      // Delay checking selection to allow for final selection update
      if (selectionTimeout) clearTimeout(selectionTimeout);
      selectionTimeout = setTimeout(() => {
        if (isSelectingRef.current && !isMouseDown) {
          isSelectingRef.current = false;
          onSelectionChange?.(false);
        }
      }, 150); // Slightly longer delay for touch to ensure selection is finalized
    };
    
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.read(() => {
          const selection = $getSelection();
          
          if ($isRangeSelection(selection)) {
            // Check if there's an actual text selection (not just cursor position)
            const hasTextSelection = !selection.isCollapsed();
            
            // If mouse/touch is down or we have text selected, consider it as "selecting"
            const isCurrentlySelecting = isMouseDown || isTouchDown || hasTextSelection;
            
            if (isCurrentlySelecting !== isSelectingRef.current) {
              isSelectingRef.current = isCurrentlySelecting;
              onSelectionChange?.(isCurrentlySelecting);
            }
          }
        });
        
        return false; // Don't prevent other selection change handlers
      },
      1 // Normal priority
    );
    
    // Add mouse and touch event listeners to track dragging
    const rootElement = editor.getRootElement();
    if (rootElement) {
      rootElement.addEventListener('mousedown', handleMouseDown);
      rootElement.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchend', handleTouchEnd);
    }
    
    return () => {
      unregister();
      if (rootElement) {
        rootElement.removeEventListener('mousedown', handleMouseDown);
        rootElement.removeEventListener('touchstart', handleTouchStart);
      }
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchend', handleTouchEnd);
      if (selectionTimeout) clearTimeout(selectionTimeout);
    };
  }, [editor, onSelectionChange]);
  
  return null;
}




export default function Editor(initialText, onUpdate, showToolbar=true, fadedBackground=false, 
      isEditable=true, transparentBackground=false, autoFocus=false, onSelectionChange=null, meetingId=null) {
  /*
  Return a markdown editor.
  initialText : the text to present to the user. It should be formated using markdown syntax.
  onUpdate : a callback called after every update on the editor.
  showToolbar : bool, whether to show or not the editor's toolbar.
  fadedbackground : bool, whether to fade the editor's background or not. 
        if transparentBackground is set to true it is not taken into account.
  isEditable : bool, whether the user can edit the content of the editor or not.
  transparentBackground: bool, whether to use or not a transparent background.
  autoFocus: bool, whether to automatically focus the editor when mounted.
  onSelectionChange: callback function to notify when user is selecting text or dragging cursor (isSelecting: boolean).
  */

  //modify the initialText to ensure that the tables are preceded and followed by an empty line 
  // (markdown syntax require it and so do the transformation from markdown to lexical nodes)
  //this regex should be somewhere else but I don't know where :/.
  initialText = initialText
                          //add a blankline before tables if its missing
                          .replace(/(?<!\n)(?<!\|)\n(?=\|)/, '\n\n')
                          //add a blankline after tables if its missing
                          .replace(/(\|.*\|)(?=\n\S)/g, '$1\n')
                          .replaceAll("\\*", '*');

  const onChangeHandler = (editorState, editor) => {
    // Handler for changes in the editor content
    try {
      editor.update(() => {
        //get the markdown string and pass it to the given onUpdate function.
        const markdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);
        onUpdate(markdown);
      })
    } catch (error) {
     console.log(error) 
    }
  };

  
  const editorConfig = {
    editorState: () => $convertFromMarkdownString(initialText, TRANSFORMERS),
    theme: Theme,
    editable: isEditable,
    // Handling of errors during update
    onError(error) {
      console.error('Lexical Editor Error:', error);
      // Don't throw the error to prevent grey screen
      // throw error;
    },
    // Any custom nodes go here
    nodes: [
      HeadingNode, // Keep the original HeadingNode
      DetailRequestButtonNode, // Register our custom DetailRequestButtonNode following the guide
      TableMenuButtonNode, // Register our table menu button node
      HorizontalRuleNode, // Add HorizontalRuleNode for --- support
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode
    ]
  };

  var toolbar = ""
  if (showToolbar){
    toolbar = <ToolbarPlugin />
  }

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="editor-container">
        {toolbar}
        <div className={`editor-inner ${fadedBackground ? 'transparent-background' : ''}`}
              style={{background : transparentBackground ? '#fff0' : 'auto'}} >
          <RichTextPlugin
            contentEditable={<ContentEditable className="editor-input" />}
            placeholder={<Placeholder />}
            ErrorBoundary={ErrorBoundary}
          >
            <OnChangePlugin onChange={onChangeHandler} />
          </RichTextPlugin>
          {autoFocus && <AutoFocusPlugin />}
          <ListPlugin />
          <OnChangePlugin onChange={onChangeHandler} />
          <LinkPlugin />
          <HistoryPlugin />
          <TablePlugin hasCellMerge={true} hasCellBackgroundColor={true} />
          <MarkdownShortcutPlugin transformers={PLAYGROUND_TRANSFORMERS} />
          <MarkdownTablePlugin />
          <TableInteractionPlugin />
          <CodeHighlightPlugin />
          <HeaderDetailRequestPlugin meetingId={meetingId} />
          <SelectionChangePlugin onSelectionChange={onSelectionChange} />
        </div>
      </div>
    </LexicalComposer>
  );
}